import { useState, useEffect, useCallback, useRef } from 'react';
import type { WebSocketMessage } from '../../../services/websocket.service';
import type { AgentMessagePart } from '../../../types/agent-messages';
import type { ChatThread, AgentChatMessage } from './types';
import type { UseWebSocketConnectionReturn } from './useWebSocketConnection';

export interface UseThreadManagerOptions {
  profileId: string;
  connection: UseWebSocketConnectionReturn;
  onThreadSelected?: (messages: AgentChatMessage[]) => void;
}

export interface UseThreadManagerReturn {
  threads: ChatThread[];
  activeSessionId: string | null;
  loadingThreads: boolean;
  loadingHistory: boolean;
  hasMoreHistory: boolean;
  fetchThreads: () => void;
  createNewThread: () => void;
  selectThread: (sessionId: string) => void;
  loadMoreHistory: () => void;
  resetSession: () => void;
}

const HANDLER_ID = 'threadManager';
const FETCH_TIMEOUT_MS = 5000;

function historyToMessages(
  messages: WebSocketMessage['messages'],
): AgentChatMessage[] {
  if (!messages) return [];
  return messages.map((msg, i) => ({
    id: `history-${msg.timestamp}-${i}`,
    role: msg.role,
    content: msg.content,
    timestamp: new Date(msg.timestamp),
    agentParts: msg.structuredParts as AgentMessagePart[] | undefined,
    toolCallCount: msg.toolCalls?.length,
  }));
}

export const useThreadManager = ({
  profileId,
  connection,
  onThreadSelected,
}: UseThreadManagerOptions): UseThreadManagerReturn => {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);

  const onThreadSelectedRef = useRef(onThreadSelected);
  onThreadSelectedRef.current = onThreadSelected;

  const oldestTimestampRef = useRef<string | null>(null);
  const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchThreads = useCallback(() => {
    setLoadingThreads(true);
    connection.sendRaw({ action: 'getThreads', profileId });

    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(() => {
      setLoadingThreads(false);
    }, FETCH_TIMEOUT_MS);
  }, [connection.sendRaw, profileId]);

  const createNewThread = useCallback(() => {
    const localId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    setActiveSessionId(localId);
    setHasMoreHistory(false);
    oldestTimestampRef.current = null;
    onThreadSelectedRef.current?.([]);
    connection.sendRaw({ action: 'newThread', profileId });
  }, [connection.sendRaw, profileId]);

  const selectThread = useCallback(
    (sessionId: string) => {
      setActiveSessionId(sessionId);
      setLoadingHistory(true);
      setHasMoreHistory(false);
      oldestTimestampRef.current = null;
      connection.sendRaw({
        action: 'getMessages',
        sessionId,
        profileId,
        limit: 100,
      });

      setTimeout(() => setLoadingHistory(false), FETCH_TIMEOUT_MS);
    },
    [connection.sendRaw, profileId],
  );

  const loadMoreHistory = useCallback(() => {
    if (!activeSessionId || !hasMoreHistory || loadingHistory) return;
    setLoadingHistory(true);
    connection.sendRaw({
      action: 'getMessages',
      sessionId: activeSessionId,
      profileId,
      limit: 100,
      before: oldestTimestampRef.current,
    });

    setTimeout(() => setLoadingHistory(false), FETCH_TIMEOUT_MS);
  }, [activeSessionId, hasMoreHistory, loadingHistory, connection.sendRaw, profileId]);

  const resetSession = useCallback(() => {
    if (activeSessionId) {
      connection.sendRaw({ action: 'reset_session', sessionId: activeSessionId });
    }
    setActiveSessionId(null);
    onThreadSelectedRef.current?.([]);
  }, [activeSessionId, connection.sendRaw]);

  useEffect(() => {
    const handler = (data: WebSocketMessage) => {
      switch (data.type) {
        case 'threads': {
          if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
            fetchTimeoutRef.current = null;
          }
          const list: ChatThread[] = (data.threads ?? []).map(t => ({
            sessionId: t.sessionId,
            title: t.title,
            createdAt: t.createdAt,
            updatedAt: t.updatedAt,
            createdBy: t.createdBy,
            status: t.status as 'active' | 'archived',
          }));
          setThreads(list);
          setLoadingThreads(false);
          break;
        }

        case 'threadCreated': {
          const newSessionId = data.sessionId!;
          const newThread: ChatThread = {
            sessionId: newSessionId,
            title: 'New Chat',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: '',
            status: 'active',
          };
          setThreads(prev => [newThread, ...prev]);
          setActiveSessionId(newSessionId);
          setHasMoreHistory(false);
          oldestTimestampRef.current = null;
          break;
        }

        case 'history': {
          const loaded = historyToMessages(data.messages);
          setHasMoreHistory(data.hasMore ?? false);
          setLoadingHistory(false);

          if (loaded.length > 0) {
            oldestTimestampRef.current = data.messages![0].timestamp;
          }

          if (!oldestTimestampRef.current || loaded.length === (data.messages?.length ?? 0)) {
            onThreadSelectedRef.current?.(loaded);
          }
          break;
        }

        case 'session_reset': {
          const resetId = data.sessionId;
          setThreads(prev =>
            prev.map(t =>
              t.sessionId === resetId ? { ...t, status: 'archived' as const } : t,
            ),
          );
          if (activeSessionId === resetId) {
            setActiveSessionId(null);
            onThreadSelectedRef.current?.([]);
          }
          break;
        }

        case 'error': {
          if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
            fetchTimeoutRef.current = null;
          }
          setLoadingThreads(false);
          setLoadingHistory(false);
          break;
        }

        default:
          break;
      }
    };

    connection.addMessageHandler(HANDLER_ID, handler);
    return () => connection.removeMessageHandler(HANDLER_ID);
  }, [connection.addMessageHandler, connection.removeMessageHandler, activeSessionId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
  }, []);

  return {
    threads,
    activeSessionId,
    loadingThreads,
    loadingHistory,
    hasMoreHistory,
    fetchThreads,
    createNewThread,
    selectThread,
    loadMoreHistory,
    resetSession,
  };
};
